import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMunicipalityId } from '../../hooks/useCouncilPath';
import { getApiClient } from '../../api';
import { redirectErrorPage } from '../../utils/errors';
import { Message, Dropdown } from '../../components/PrimereactComponents';
import { useTranslation } from 'react-i18next';
import LoadingPage from '../../components/LoadingPage';
import { URLKeys } from '../../Constants';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import ComparisonDetail from '../../components/membersComparison/ComparisonDetail';
import './MembersComparison.css';
import type { DropdownOptions } from '../../api';

const MembersComparison = () => {
    const [loading, setLoading] = useState(true);
    const searchParams = new URLSearchParams(window.location.search);
    let firstMemberValue = searchParams.get(URLKeys.firstMember) || null;
    let secondMemberValue = searchParams.get(URLKeys.secondMember) || null;

    const [firstMember, setFirstMember] = useState<DropdownOptions | null>(null);
    const [secondMember, setSecondMember] = useState<DropdownOptions | null>(null);

    const { bodyType, zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const { t } = useTranslation('analyses');

    const [memberOptions, setMemberOptions] = useState<DropdownOptions[] | undefined>(undefined);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const api = getApiClient();
                const memberOptionsData = await api.getDropdownMemberOptionsByMunicipalityIdAndBodyAndCouncilId(
                    municipalitaId ?? '',
                    bodyType ?? '',
                    parseInt(zastupitelstvoId ?? '0', 10)
                );
                setMemberOptions(memberOptionsData);
                setFirstMember(
                    firstMemberValue !== null && memberOptionsData
                        ? memberOptionsData.find((member: DropdownOptions) => member.id === parseInt(firstMemberValue)) ?? null
                        : null
                );
                setSecondMember(
                    secondMemberValue !== null && memberOptionsData
                        ? memberOptionsData.find((member: DropdownOptions) => member.id === parseInt(secondMemberValue)) ?? null
                        : null
                );
            } catch (error) {
                redirectErrorPage(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [bodyType, firstMemberValue, municipalitaId, secondMemberValue, zastupitelstvoId]);

    if (loading) {
        return <LoadingPage />;
    }

    const setParams = (key: string, value: string | number): void => {
        const url = new URL(window.location.toString());
        url.searchParams.set(key, value.toString());
        window.history.replaceState({}, '', url);
    };
    const clearParams = (key: string): void => {
        const url = new URL(window.location.toString());
        url.searchParams.delete(key);
        window.history.replaceState({}, '', url);
    };

    const onFirstMemberChange = (e: { value: DropdownOptions }) => {
        setFirstMember(e.value);
        if (e.value !== undefined) {
            setParams(URLKeys.firstMember, e?.value.id);
        } else {
            setFirstMember(null);
            clearParams(URLKeys.firstMember);
        }
    };

    const onSecondMemberChange = (e: { value: DropdownOptions }) => {
        setSecondMember(e.value);
        if (e.value !== undefined) {
            setParams(URLKeys.secondMember, e.value.id);
        } else {
            setSecondMember(null);
            clearParams(URLKeys.secondMember);
        }
    };

    if (memberOptions === undefined) {
        return <LoadingPage />;
    }

    return (
        <>
            <ContentSection
                title={t('comparison.councilMembers')}
                level={1}
                subtitle={t('comparison.councilMembersTooltip')}
                className='page-title-section'
            />
            <ContentSection level={3} contentClassName='members-comparison-analyses-selection-wrapper'>
                <span className='p-float-label'>
                    <Dropdown
                        value={firstMember}
                        options={memberOptions.filter((option) => option.id !== secondMember?.id)}
                        onChange={onFirstMemberChange}
                        optionLabel='name'
                        filter
                        showClear
                        filterBy='name'
                        inputId='firstMemberSelection'
                    />
                    <label htmlFor='firstMemberSelection'>{t('comparison.selectFirstMember')}</label>
                </span>
                <span className='p-float-label'>
                    <Dropdown
                        value={secondMember}
                        options={memberOptions.filter((option) => option.id !== firstMember?.id)}
                        onChange={onSecondMemberChange}
                        optionLabel='name'
                        filter
                        showClear
                        filterBy='name'
                        inputId='secondMemberSelection'
                    />
                    <label htmlFor='secondMemberSelection'>{t('comparison.selectSecondMember')}</label>
                </span>
            </ContentSection>
            {firstMember === null || secondMember === null ? (
                <Message severity='info' text={t('comparison.selectTwoDifferent')} style={{ margin: '0 auto' }} />
            ) : (
                <ComparisonDetail firstMemberId={firstMember.id} secondMemberId={secondMember.id} />
            )}
        </>
    );
};

export default MembersComparison;
